import { HoverText } from '@campfire/hover-link/lib';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { Box, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { AssignmentTurnedIn, CheckCircle, ChevronRight, OpenInNew } from '@material-ui/icons';
import { convertFromRaw, EditorState } from 'draft-js';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const SUCCESS_TINT = 'rgba(76, 175, 80, 0.05)';
const SUCCESS_GREEN = '#4caf50';
const DESCRIPTION_CLAMP_HEIGHT = 66;

export interface SelfDirectedAction {
  sessionId: string;
  name: string;
  description?: string | null;
  detail?: string | null;
  externalLink?: string | null;
  mandatory: boolean;
  completed: boolean;
}

interface SelfDirectedActivityDialogProps {
  open: boolean;
  onClose: () => void;
  activityName: string;
  description?: string | null;
  availableUntil?: string | null;
  enrolled: boolean;
  actions: SelfDirectedAction[];
  joining?: boolean;
  onJoin: () => void;
  onOpenAction: (action: SelfDirectedAction) => void;
  onMarkComplete: (action: SelfDirectedAction) => void;
}

const formatAvailableUntil = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  const date = DateTime.fromISO(isoDate);
  return date.isValid ? date.toFormat('d MMMM yyyy') : null;
};

export const SelfDirectedActivityDialog = (props: SelfDirectedActivityDialogProps) => {
  const {
    open,
    onClose,
    activityName,
    description,
    availableUntil,
    enrolled,
    actions,
    joining,
    onJoin,
    onOpenAction,
    onMarkComplete,
  } = props;
  const { isMobile, theme } = useCampfireTheme();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const mandatoryActions = useMemo(() => actions.filter((action) => action.mandatory), [actions]);
  const completedCount = useMemo(() => mandatoryActions.filter((action) => action.completed).length, [
    mandatoryActions,
  ]);
  const totalCount = mandatoryActions.length;
  const allComplete = totalCount > 0 && completedCount === totalCount;
  const showProgress = enrolled && totalCount > 0;

  const availableUntilLabel = formatAvailableUntil(availableUntil);
  const actionsLabel = actions.length === 1 ? 'Action required' : 'Actions required';
  const secondaryText = theme.color.grey.neutral200;

  const descriptionEditorState = useMemo(() => {
    if (!description) return null;
    try {
      return EditorState.createWithContent(convertFromRaw(JSON.parse(description)));
    } catch (_unusedError) {
      return null;
    }
  }, [description]);

  const isLongDescription = (descriptionEditorState?.getCurrentContent().getPlainText().length ?? 0) > 160;

  const renderDescription = () => {
    if (descriptionEditorState) {
      return (
        <Box mb={2}>
          <Box
            style={{
              color: theme.color.grey.neutral400,
              fontSize: 14,
              lineHeight: '22px',
              maxHeight: isLongDescription && !descriptionExpanded ? DESCRIPTION_CLAMP_HEIGHT : undefined,
              overflow: 'hidden',
            }}
          >
            <CampfireRichEditor editorState={descriptionEditorState} readOnly />
          </Box>
          {isLongDescription ? (
            <HoverText
              variant='body2'
              color='primary'
              onClick={() => setDescriptionExpanded((value) => !value)}
              style={{ marginTop: 2, fontWeight: 600 }}
            >
              {descriptionExpanded ? 'Show less' : 'Show more'}
            </HoverText>
          ) : null}
        </Box>
      );
    }
    if (description) {
      return (
        <Typography variant='body2' style={{ color: secondaryText, lineHeight: 1.6, marginBottom: 16 }}>
          {description}
        </Typography>
      );
    }
    return null;
  };

  return (
    <FullscreenDialog
      open={open}
      close={onClose}
      fullScreen={isMobile}
      title={activityName}
      scroll='body'
      maxWidth='md'
    >
      {allComplete ? (
        <>
          <DialogContent
            style={{ minWidth: isMobile ? 290 : 560, textAlign: 'center', paddingTop: 32, paddingBottom: 8 }}
          >
            <CheckCircle style={{ fontSize: 48, color: SUCCESS_GREEN, marginBottom: 8 }} />
            <Typography style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Activity complete</Typography>
            <Typography variant='body2' style={{ color: secondaryText, maxWidth: 360, margin: '0 auto' }}>
              Thank you for completing this activity.
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px', justifyContent: 'center' }}>
            <TabletButton
              size='medium'
              variant='contained'
              color='primary'
              onClick={onClose}
              style={{ textTransform: 'none' }}
            >
              Done
            </TabletButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent style={{ minWidth: isMobile ? 290 : 560, paddingTop: 20 }}>
            {renderDescription()}

            {availableUntilLabel ? (
              <Typography variant='body2' style={{ color: secondaryText }}>
                {`Available until ${availableUntilLabel}`}
              </Typography>
            ) : null}

            <Box
              mt={2.5}
              mb={1.25}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              style={{ gap: 12 }}
            >
              <Typography
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: secondaryText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {actionsLabel}
              </Typography>
              {showProgress ? (
                <Box display='flex' alignItems='center' flexShrink={0} style={{ gap: 8 }}>
                  <Typography style={{ fontSize: 12, fontWeight: 600, color: theme.color.grey.neutral400 }}>
                    {`${completedCount}/${totalCount}`}
                  </Typography>
                  <Box width={72} height={5} borderRadius={3} bgcolor='#e5e7eb' overflow='hidden'>
                    <Box
                      height='100%'
                      borderRadius={3}
                      bgcolor={theme.palette.primary.main}
                      style={{ width: `${(completedCount / totalCount) * 100}%`, transition: 'width 0.2s' }}
                    />
                  </Box>
                </Box>
              ) : null}
            </Box>

            {actions.map((action) => {
              const clickable = enrolled && !action.completed;
              return (
                <Box
                  key={action.sessionId}
                  display='flex'
                  alignItems='center'
                  border={`1px solid ${action.completed ? SUCCESS_GREEN : theme.color.grey.border}`}
                  borderRadius={12}
                  bgcolor={action.completed ? SUCCESS_TINT : '#fff'}
                  px={2}
                  py={1.75}
                  mb={1.25}
                  style={{
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'border-color 0.15s, background-color 0.15s',
                  }}
                  onClick={clickable ? () => onOpenAction(action) : undefined}
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='50%'
                    width={40}
                    height={40}
                    flexShrink={0}
                    mr={1.75}
                    bgcolor={action.completed ? 'rgba(76, 175, 80, 0.1)' : theme.color.grey.neutralBrand100}
                  >
                    {action.completed ? (
                      <CheckCircle style={{ color: SUCCESS_GREEN }} />
                    ) : action.externalLink ? (
                      <OpenInNew style={{ color: secondaryText }} />
                    ) : (
                      <AssignmentTurnedIn style={{ color: secondaryText }} />
                    )}
                  </Box>
                  <Box flex='1 1 auto' minWidth={0}>
                    <Typography
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: action.completed ? theme.color.grey.neutral200 : theme.color.grey.neutral400,
                        lineHeight: 1.3,
                      }}
                    >
                      {action.name}
                    </Typography>
                    {action.description && !action.completed ? (
                      <Typography
                        style={{
                          fontSize: 12.5,
                          color: secondaryText,
                          lineHeight: 1.4,
                          marginTop: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {action.description}
                      </Typography>
                    ) : null}
                    <Typography
                      style={{
                        fontSize: 11.5,
                        color: action.completed ? SUCCESS_GREEN : theme.color.grey.neutral300,
                        marginTop: 1,
                      }}
                    >
                      {action.completed
                        ? 'Completed'
                        : action.detail || (action.externalLink ? 'Opens an external link' : 'Submit a report')}
                    </Typography>
                  </Box>
                  {clickable && action.externalLink ? (
                    <TabletButton
                      size='small'
                      variant='outlined'
                      color='primary'
                      onClick={(event) => {
                        event.stopPropagation();
                        onMarkComplete(action);
                      }}
                      style={{ textTransform: 'none', flexShrink: 0, marginLeft: 8 }}
                    >
                      {'Mark as complete'}
                    </TabletButton>
                  ) : clickable ? (
                    <ChevronRight style={{ color: theme.color.grey.neutral200, flexShrink: 0 }} />
                  ) : null}
                </Box>
              );
            })}
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid ${theme.color.grey.border}` }}>
            <TabletButton size='medium' onClick={onClose} style={{ textTransform: 'none', marginRight: 8 }}>
              {enrolled ? "I'll do this later" : 'Dismiss'}
            </TabletButton>
            {!enrolled ? (
              <TabletButton
                size='medium'
                variant='contained'
                color='primary'
                onClick={onJoin}
                disabled={joining}
                style={{ textTransform: 'none' }}
              >
                {joining ? 'Joining…' : 'Join'}
              </TabletButton>
            ) : null}
          </DialogActions>
        </>
      )}
    </FullscreenDialog>
  );
};
